import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Pencil, PencilLine, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { clampPageInput, pageNumberToString } from "@/utils/fields";
import type { IExtractedPropertyValue } from "@/api/documents/documents.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EditablePropertyRowProps {
    property: IExtractedPropertyValue;
    isEditable: boolean;
    onSave: (
        propertyId: number,
        value: string | null,
        pageNumber: number | null
    ) => void;
    onDelete: (propertyId: number) => void;
    /** Optional PDF viewer page-navigation plugin instance */
    pageNavigationPluginInstance?: {
        jumpToPage: (pageIndex: number) => void;
    };
    /** Total pages in the PDF, used for page number input validation */
    totalPages?: number;
    /** Whether to show the "Edited" badge. Defaults to true.
     *  Set to false for custom field properties (user-created, not AI overrides). */
    showEditedBadge?: boolean;
}

// ---------------------------------------------------------------------------
// Internal state machine
// ---------------------------------------------------------------------------

type RowMode = "read" | "edit";

const DELETE_CONFIRM_TIMEOUT_MS = 2000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * EditablePropertyRow
 *
 * Replaces the read-only PropertyRow in the scope extracted-fields panel.
 * Supports inline editing, delete confirmation, page navigation, and an
 * "Edited" badge for manually overridden values.
 *
 * Usage:
 * ```tsx
 * <EditablePropertyRow
 *   property={property}
 *   isEditable={canEdit}
 *   onSave={(id, value, pageNumber) => mutation.mutate({ id, value, pageNumber })}
 *   onDelete={(id) => deleteMutation.mutate(id)}
 *   pageNavigationPluginInstance={pageNavigationPlugin}
 * />
 * ```
 */
export function EditablePropertyRow({
    property,
    isEditable,
    onSave,
    onDelete,
    pageNavigationPluginInstance,
    totalPages,
    showEditedBadge = true,
}: EditablePropertyRowProps) {
    const [mode, setMode] = useState<RowMode>("read");
    const [draftValue, setDraftValue] = useState<string>(
        property.raw_value ?? property.value ?? ""
    );
    const [draftPage, setDraftPage] = useState<string>(
        pageNumberToString(property.page_number)
    );
    const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const deleteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep drafts in sync when the property changes from outside (e.g. optimistic update)
    useEffect(() => {
        if (mode === "read") {
            setDraftValue(property.raw_value ?? property.value ?? "");
            setDraftPage(pageNumberToString(property.page_number));
        }
    }, [property.raw_value, property.value, property.page_number, mode]);

    // Auto-focus input when entering edit mode
    useEffect(() => {
        if (mode === "edit") {
            // Defer one frame so the input is mounted and measured
            requestAnimationFrame(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            });
        }
    }, [mode]);

    // Clean up delete timer on unmount
    useEffect(() => {
        return () => {
            if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
        };
    }, []);

    // -----------------------------------------------------------------------
    // Derived state
    // -----------------------------------------------------------------------

    const hasValue = property.value !== null && property.value !== "";
    const isEdited = property.is_edited === true;
    const isNotFound = !hasValue;
    const isCustom = property.source === "custom";

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------

    const commitSave = useCallback(() => {
        const trimmed = draftValue.trim();
        const normalized = trimmed === "" ? null : trimmed;
        const pageNum = draftPage.trim() ? parseInt(draftPage, 10) : null;
        const inRange =
            pageNum !== null &&
            !isNaN(pageNum) &&
            pageNum >= 1 &&
            (!totalPages || pageNum <= totalPages);
        const normalizedPage = inRange ? pageNum : null;
        // Compare against raw_value (the edit seed), not the formatted display value
        const original = property.raw_value ?? property.value ?? null;
        const originalPage = property.page_number ?? null;
        if (normalized !== original || normalizedPage !== originalPage) {
            onSave(property.id, normalized, normalizedPage);
        }
        setMode("read");
    }, [
        draftValue,
        draftPage,
        totalPages,
        onSave,
        property.id,
        property.raw_value,
        property.value,
        property.page_number,
    ]);

    const cancelEdit = useCallback(() => {
        setDraftValue(property.raw_value ?? property.value ?? "");
        setDraftPage(pageNumberToString(property.page_number));
        setMode("read");
    }, [property.raw_value, property.value, property.page_number]);

    const handleValueClick = useCallback(() => {
        if (!isEditable || mode === "edit") return;
        setDraftValue(property.raw_value ?? property.value ?? "");
        setDraftPage(pageNumberToString(property.page_number));
        setMode("edit");
    }, [
        isEditable,
        mode,
        property.raw_value,
        property.value,
        property.page_number,
    ]);

    const handleInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                commitSave();
            } else if (e.key === "Escape") {
                e.preventDefault();
                cancelEdit();
            }
        },
        [commitSave, cancelEdit]
    );

    const handleInputBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            // Don't save if focus is moving to another input within the same edit row
            const row = e.currentTarget.closest('[role="row"]');
            if (row && e.relatedTarget && row.contains(e.relatedTarget)) return;
            // Only save on blur when still in edit mode. Escape sets mode to
            // "read" before blur fires, so cancelEdit won't double-trigger.
            if (mode === "edit") {
                commitSave();
            }
        },
        [mode, commitSave]
    );

    const handleDeleteClick = useCallback(() => {
        if (!isDeleteConfirming) {
            setIsDeleteConfirming(true);
            deleteTimerRef.current = setTimeout(() => {
                setIsDeleteConfirming(false);
            }, DELETE_CONFIRM_TIMEOUT_MS);
        } else {
            if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
            setIsDeleteConfirming(false);
            onDelete(property.id);
        }
    }, [isDeleteConfirming, onDelete, property.id]);

    const handlePageChipClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!property.page_number || !pageNavigationPluginInstance) return;
            pageNavigationPluginInstance.jumpToPage(property.page_number - 1);
        },
        [property.page_number, pageNavigationPluginInstance]
    );

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const pageChip = property.page_number ? (
        <button
            type="button"
            onClick={handlePageChipClick}
            className={cn(
                "border-border bg-muted text-muted-foreground inline-flex h-5 shrink-0 items-center rounded-full border px-1.5 font-mono text-[10px] font-medium transition-colors",
                pageNavigationPluginInstance &&
                    "hover:bg-accent hover:text-accent-foreground cursor-pointer"
            )}
            aria-label={`Go to page ${property.page_number}`}
        >
            p.{property.page_number}
        </button>
    ) : null;

    // -----------------------------------------------------------------------
    // EDIT mode
    // -----------------------------------------------------------------------

    if (mode === "edit") {
        return (
            <div
                className="group/row flex min-h-[32px] w-full items-center gap-2 rounded-md px-1 py-1"
                role="row"
                aria-label={`Editing ${property.property_name}`}
            >
                {/* Property name, compact in edit mode */}
                <div className="min-w-0 shrink-0" style={{ maxWidth: "35%" }}>
                    <span className="text-foreground flex items-center gap-1 text-sm font-medium">
                        <span className="truncate">
                            {property.property_name}
                        </span>
                        {isCustom && (
                            <PencilLine
                                size={10}
                                className="text-muted-foreground/50 shrink-0"
                                aria-label="User-added property"
                            />
                        )}
                    </span>
                </div>

                {/* Inline editor, takes remaining space */}
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <Input
                        ref={inputRef}
                        value={draftValue}
                        onChange={(e) => setDraftValue(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleInputBlur}
                        className="h-7 flex-1 text-sm"
                        aria-label={`Value for ${property.property_name}`}
                        placeholder="Enter value…"
                    />
                    <Input
                        type="number"
                        placeholder="Pg"
                        min={1}
                        max={totalPages}
                        value={draftPage}
                        onChange={(e) => {
                            const clamped = clampPageInput(
                                e.target.value,
                                totalPages
                            );
                            if (clamped !== null) setDraftPage(clamped);
                        }}
                        onKeyDown={handleInputKeyDown}
                        onBlur={handleInputBlur}
                        className="h-7 w-14 shrink-0 [appearance:textfield] text-center text-xs [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        aria-label={`Page number for ${property.property_name}`}
                    />
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // READ / NOT FOUND / DISABLED mode
    // -----------------------------------------------------------------------

    return (
        <div
            className={cn(
                "group/row flex min-h-[32px] w-full items-center gap-2 rounded-md px-1 py-1",
                isEditable && "hover:bg-muted/60 transition-colors duration-100"
            )}
            onMouseLeave={() => {
                if (isDeleteConfirming) {
                    if (deleteTimerRef.current)
                        clearTimeout(deleteTimerRef.current);
                    setIsDeleteConfirming(false);
                }
            }}
            role="row"
            aria-label={property.property_name}
        >
            {/* Left: property name + optional field context + custom marker */}
            <div className="min-w-0 flex-[2]">
                <span className="text-foreground flex items-center gap-1 text-sm font-medium">
                    <span className="truncate">{property.property_name}</span>
                    {isCustom && (
                        <PencilLine
                            size={10}
                            className="text-muted-foreground/40 shrink-0 transition-opacity duration-150 group-hover/row:opacity-70"
                            aria-label="User-added property"
                        />
                    )}
                </span>
            </div>

            {/* Right: value area + page chip + badges + trash */}
            <div className="flex flex-1 items-center justify-end gap-1.5">
                {/* Value / Not found */}
                <button
                    type="button"
                    onClick={handleValueClick}
                    disabled={!isEditable}
                    className={cn(
                        "text-muted-foreground min-w-0 text-right text-sm break-words",
                        isEditable &&
                            isNotFound &&
                            "hover:text-foreground italic transition-colors",
                        isEditable &&
                            !isNotFound &&
                            "hover:bg-accent hover:text-accent-foreground cursor-pointer rounded px-1 py-0.5 transition-colors",
                        !isEditable && "cursor-default"
                    )}
                    aria-label={
                        isNotFound
                            ? `${property.property_name}: not found, click to add`
                            : `Edit ${property.property_name}`
                    }
                >
                    {isNotFound ? (
                        <span className="flex items-center gap-1">
                            Not found
                            {isEditable && (
                                <Pencil
                                    className="inline-block opacity-0 transition-opacity group-hover/row:opacity-40"
                                    size={11}
                                    aria-hidden
                                />
                            )}
                        </span>
                    ) : (
                        property.value
                    )}
                </button>

                {/* Page chip */}
                {pageChip}

                {/* "Edited" badge, shown when value was manually set (suppressed for custom fields) */}
                {showEditedBadge && isEdited && !isNotFound && (
                    <Badge
                        variant="secondary"
                        className="hidden shrink-0 text-[10px] group-hover/row:hidden sm:inline-flex"
                        aria-label="Value was manually edited"
                    >
                        Edited
                    </Badge>
                )}

                {/* Trash / confirm delete, only when editable and row hovered */}
                {isEditable && (
                    <Button
                        variant={isDeleteConfirming ? "destructive" : "ghost"}
                        size="icon-xs"
                        onClick={handleDeleteClick}
                        className={cn(
                            "shrink-0 transition-opacity duration-150",
                            isDeleteConfirming
                                ? "opacity-100"
                                : "opacity-0 group-hover/row:opacity-60 group-hover/row:hover:opacity-100"
                        )}
                        aria-label={
                            isDeleteConfirming
                                ? `Confirm delete ${property.property_name}`
                                : `Delete ${property.property_name}`
                        }
                    >
                        {isDeleteConfirming ? (
                            <Check size={12} aria-hidden />
                        ) : (
                            <Trash2 size={12} aria-hidden />
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}
